import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useGetMany, useRecordContext, type RaRecord } from "react-admin";

import { callApi } from "@/dataprovider/misc-apis";
import {
  buildMiqaatBotChatQrValue,
  resolveMiqaatBotWhatsappPhone,
} from "@/utils/whatsapp-chat-url";

import MiqaatBotChatPoster from "./MiqaatBotChatPoster";

/**
 * One poster per linked venue, all encoding the same bot chat QR.
 */
export default function MiqaatVenuePosters() {
  const record = useRecordContext<RaRecord>();
  const rawVenueIds = record?.venueIds;
  const venueIds = Array.isArray(rawVenueIds) ? (rawVenueIds as string[]).filter(Boolean) : [];
  const { data: venues, isLoading } = useGetMany(
    "miqaatVenues",
    { ids: venueIds },
    { enabled: venueIds.length > 0 }
  );
  const [botPhone, setBotPhone] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await callApi({
          location: "tenantRuntimeConfig/whatsappBotPhone",
          method: "GET",
        });
        const phone = (response.data as { whatsappBotPhone?: string | null } | undefined)
          ?.whatsappBotPhone;
        if (!cancelled) {
          setBotPhone(resolveMiqaatBotWhatsappPhone(phone));
        }
      } catch {
        if (!cancelled) {
          setBotPhone(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const chatUrl = botPhone === undefined ? null : buildMiqaatBotChatQrValue(botPhone);

  if (!record) {
    return null;
  }

  const miqaatName = record.name ? String(record.name) : null;
  const posters =
    venueIds.length > 0
      ? venueIds.map((id) => {
          const venue = (venues ?? []).find((v) => String(v.id) === id);
          return {
            key: id,
            label: venue?.name ? String(venue.name) : id,
          };
        })
      : [
          {
            key: "fallback",
            label: record.venue?.trim() ? String(record.venue) : "Check-in",
          },
        ];

  return (
    <Box sx={{ width: "100%", maxWidth: 1200 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
      >
        Check-in posters
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Same WhatsApp bot QR for every venue — label the printout with the venue name.
      </Typography>
      {isLoading && venueIds.length > 0 ? (
        <Typography variant="body2" color="text.secondary">
          Loading venues…
        </Typography>
      ) : botPhone === undefined ? (
        <Typography variant="body2" color="text.secondary">
          Loading bot number…
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {posters.map((p) => (
            <MiqaatBotChatPoster
              key={p.key}
              venueLabel={p.label}
              chatUrl={chatUrl}
              miqaatName={miqaatName}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
